import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { FileUp, Undo2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import toast from "react-hot-toast";
import {
  Badge,
  Button,
  ConfirmModal,
  Modal,
  ModalFooter,
} from "@/components/ui";
import {
  DataTable,
  type DataTableColumn,
} from "@/components/table";
import { FileDropzone } from "@/components/common/FileDropzone";
import { EmptyState } from "@/components/ui";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import { cn } from "@/lib/utils";

const MAX_UNDO_STEPS = 50;

export interface ImportSectionLabels {
  title: string;
  barHint: string;
  adminBadge: string;
  open: string;
  modalDescription: string;
  cancel: string;
  close: string;
  extract: string;
  save: string;
  dropHint: string;
  fileLabel: string;
  previewCount: (count: number) => string;
  chooseAnother: string;
  emptyPreview: string;
  pickFile?: string;
  /** Undo last edit/delete/add in the preview table. */
  undo?: string;
  /** Confirm before discarding preview work (choose another PDF / close). */
  discardConfirmTitle?: string;
  discardConfirmMessage?: string;
  discardConfirmAction?: string;
  discardConfirmCancel?: string;
  /** Shown on the dropzone while extraction is running. */
  extractingHint?: string;
  /** Secondary line when a file is selected (e.g. “Click to change”). */
  fileSelectedHint?: string;
}

export interface ImportRowActions<TRow extends { clientKey: string }> {
  updateRow: (
    clientKey: string,
    patch: Partial<Omit<TRow, "clientKey">>,
  ) => void;
  removeRow: (clientKey: string) => void;
  isBusy: boolean;
}

export interface ImportPreviewToolbarContext<TRow extends { clientKey: string }>
  extends ImportRowActions<TRow> {
  resetImportState: () => void;
  appendRow: (row: TRow) => void;
  undo: () => void;
  canUndo: boolean;
}

export interface ImportSectionProps<
  TRow extends { clientKey: string },
  TSort extends string,
> {
  entityKey: string;
  preferencesKey: string;
  defaultSortColumn: TSort;
  labels: ImportSectionLabels;
  buildColumns: (
    actions: ImportRowActions<TRow>,
  ) => DataTableColumn<TRow, TSort>[];
  onExtract: (file: File) => Promise<TRow[]>;
  onExtractSuccess?: (rows: TRow[]) => void;
  onExtractError?: (error: unknown) => void;
  onConfirm: (rows: TRow[]) => Promise<void>;
  onConfirmError?: (error: unknown) => void;
  canSave?: (rows: TRow[]) => boolean;
  validateBeforeSave?: (rows: TRow[]) => boolean;
  previewTopContent?: ReactNode | ((ctx: { isBusy: boolean; rows: TRow[] }) => ReactNode);
  previewToolbarTrailing?: (
    ctx: ImportPreviewToolbarContext<TRow>,
  ) => ReactNode;
  rowClassName?: string | ((row: TRow) => string | undefined);
  onResetImportState?: () => void;
  /** Modal width on the file-pick step. */
  modalSize?: "lg" | "xl" | "full";
  /** Modal width after extraction when the preview table is shown. */
  previewModalSize?: "xl" | "full";
}

export function ImportSection<
  TRow extends { clientKey: string },
  TSort extends string,
>({
  entityKey,
  preferencesKey,
  defaultSortColumn,
  labels,
  buildColumns,
  onExtract,
  onExtractSuccess,
  onExtractError,
  onConfirm,
  onConfirmError,
  canSave,
  validateBeforeSave,
  previewTopContent,
  previewToolbarTrailing,
  rowClassName,
  onResetImportState,
  modalSize = "xl",
  previewModalSize = "full",
}: ImportSectionProps<TRow, TSort>) {
  const { t } = useTranslation("common");
  const tableLabels = buildDataTableLabels(t);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [draftRows, setDraftRows] = useState<TRow[] | null>(null);
  const [undoStack, setUndoStack] = useState<TRow[][]>([]);
  const draftRowsRef = useRef<TRow[] | null>(null);
  draftRowsRef.current = draftRows;
  const coalesceEditKeyRef = useRef<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const pendingDiscardActionRef = useRef<"reset" | "close" | null>(null);

  const resetImportState = useCallback(() => {
    setDraftRows(null);
    draftRowsRef.current = null;
    setUndoStack([]);
    coalesceEditKeyRef.current = null;
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onResetImportState?.();
  }, [onResetImportState]);

  const isBusy = isExtracting || isConfirming;
  const hasPreview = draftRows !== null;
  const hasDiscardConfirmCopy = Boolean(
    labels.discardConfirmTitle &&
      labels.discardConfirmMessage &&
      labels.discardConfirmAction &&
      labels.discardConfirmCancel,
  );

  const requestDiscard = useCallback(
    (action: "reset" | "close") => {
      if (isBusy) return;
      if (hasPreview && hasDiscardConfirmCopy) {
        pendingDiscardActionRef.current = action;
        setDiscardConfirmOpen(true);
        return;
      }
      if (action === "close") {
        setModalOpen(false);
      }
      resetImportState();
    },
    [hasDiscardConfirmCopy, hasPreview, isBusy, resetImportState],
  );

  const closeModal = useCallback(() => {
    requestDiscard("close");
  }, [requestDiscard]);

  const confirmDiscard = useCallback(() => {
    const action = pendingDiscardActionRef.current;
    pendingDiscardActionRef.current = null;
    setDiscardConfirmOpen(false);
    if (action === "close") {
      setModalOpen(false);
    }
    resetImportState();
  }, [resetImportState]);

  const pushUndoSnapshot = useCallback((current: TRow[]) => {
    setUndoStack((stack) => [...stack.slice(-(MAX_UNDO_STEPS - 1)), current]);
  }, []);

  const commitDraftChange = useCallback(
    (
      updater: (rows: TRow[]) => TRow[],
      options?: { coalesceKey?: string },
    ) => {
      const current = draftRowsRef.current;
      if (!current) return;
      const coalesceKey = options?.coalesceKey;
      if (coalesceKey) {
        if (coalesceEditKeyRef.current !== coalesceKey) {
          pushUndoSnapshot(current);
          coalesceEditKeyRef.current = coalesceKey;
        }
      } else {
        pushUndoSnapshot(current);
        coalesceEditKeyRef.current = null;
      }
      const next = updater(current);
      draftRowsRef.current = next;
      setDraftRows(next);
    },
    [pushUndoSnapshot],
  );

  const updateRow = useCallback(
    (clientKey: string, patch: Partial<Omit<TRow, "clientKey">>) => {
      const fieldKey = Object.keys(patch).sort().join(",");
      commitDraftChange(
        (rows) =>
          rows.map((row) =>
            row.clientKey === clientKey ? { ...row, ...patch } : row,
          ),
        { coalesceKey: `${clientKey}:${fieldKey}` },
      );
    },
    [commitDraftChange],
  );

  const removeRow = useCallback(
    (clientKey: string) => {
      commitDraftChange((rows) =>
        rows.filter((row) => row.clientKey !== clientKey),
      );
    },
    [commitDraftChange],
  );

  const appendRow = useCallback(
    (row: TRow) => {
      commitDraftChange((rows) => [...rows, row]);
    },
    [commitDraftChange],
  );

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (stack.length === 0) return stack;
      const previous = stack[stack.length - 1];
      draftRowsRef.current = previous;
      setDraftRows(previous);
      coalesceEditKeyRef.current = null;
      return stack.slice(0, -1);
    });
  }, []);

  const canUndo = undoStack.length > 0 && !isBusy;

  const rowActions = useMemo(
    (): ImportRowActions<TRow> => ({
      updateRow,
      removeRow,
      isBusy,
    }),
    [updateRow, removeRow, isBusy],
  );

  const columns = useMemo(
    () => buildColumns(rowActions),
    [buildColumns, rowActions],
  );

  const handleExtract = async () => {
    if (!selectedFile) {
      if (labels.pickFile) {
        toast.error(labels.pickFile);
      }
      return;
    }
    setIsExtracting(true);
    try {
      const rows = await onExtract(selectedFile);
      draftRowsRef.current = rows;
      setUndoStack([]);
      coalesceEditKeyRef.current = null;
      setDraftRows(rows);
      onExtractSuccess?.(rows);
    } catch (error) {
      onExtractError?.(error);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!draftRows || draftRows.length === 0) {
      toast.error(labels.emptyPreview);
      return;
    }
    if (validateBeforeSave && !validateBeforeSave(draftRows)) {
      return;
    }
    if (canSave && !canSave(draftRows)) {
      return;
    }
    setIsConfirming(true);
    try {
      await onConfirm(draftRows);
      setModalOpen(false);
      resetImportState();
    } catch (error) {
      onConfirmError?.(error);
    } finally {
      setIsConfirming(false);
    }
  };

  const saveEnabled =
    hasPreview &&
    draftRows.length > 0 &&
    !isBusy &&
    (canSave ? canSave(draftRows) : true);

  const toolbarContext: ImportPreviewToolbarContext<TRow> = {
    ...rowActions,
    resetImportState: () => requestDiscard("reset"),
    appendRow,
    undo,
    canUndo,
  };

  return (
    <>
      <div
        className={cn(
          "mb-6 flex flex-col gap-2 rounded-lg border border-surface-200/80",
          "bg-surface-50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4",
        )}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <FileUp
            className="h-4 w-4 shrink-0 text-surface-400"
            aria-hidden
          />
          <span className="text-sm font-medium text-surface-800">
            {labels.title}
          </span>
          <Badge variant="outline" size="sm" className="font-normal">
            {labels.adminBadge}
          </Badge>
          <span className="text-surface-300">·</span>
          <span className="text-sm text-surface-500">{labels.barHint}</span>
        </div>

        <div className="flex shrink-0">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setModalOpen(true)}
          >
            {labels.open}
          </Button>
        </div>
      </div>

      <Modal
        closeLabel={t("close")}
        isOpen={modalOpen}
        onClose={closeModal}
        title={labels.title}
        description={labels.modalDescription}
        size={hasPreview ? previewModalSize : modalSize}
        dismissOnBackdrop={!isBusy && !discardConfirmOpen}
        showCloseButton={!isBusy}
        footer={
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isBusy}
              onClick={closeModal}
            >
              {hasPreview ? labels.cancel : labels.close}
            </Button>
            {hasPreview ? (
              <Button
                type="button"
                disabled={!saveEnabled}
                isLoading={isConfirming}
                onClick={() => void handleSave()}
              >
                {labels.save}
              </Button>
            ) : (
              <Button
                type="button"
                disabled={isBusy || !selectedFile}
                isLoading={isExtracting}
                onClick={() => void handleExtract()}
              >
                {labels.extract}
              </Button>
            )}
          </ModalFooter>
        }
      >
        {!hasPreview ? (
          <div className="space-y-4">
            <FileDropzone
              id={`import-pdf-${entityKey}`}
              accept={FILE_ACCEPT.pdfOnly}
              disabled={isBusy}
              isLoading={isExtracting}
              isSelected={Boolean(selectedFile) && !isExtracting}
              inputRef={fileInputRef}
              resetInputOnSelect={false}
              title={
                isExtracting
                  ? (labels.extractingHint ?? labels.dropHint)
                  : selectedFile
                    ? selectedFile.name
                    : labels.dropHint
              }
              description={
                isExtracting
                  ? undefined
                  : selectedFile
                    ? (labels.fileSelectedHint ?? labels.fileLabel)
                    : labels.fileLabel
              }
              onFilesSelected={(files) => {
                setSelectedFile(files[0] ?? null);
              }}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {typeof previewTopContent === "function"
              ? previewTopContent({ isBusy, rows: draftRows })
              : previewTopContent}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="shrink-0 text-sm text-surface-600">
                {labels.previewCount(draftRows.length)}
              </p>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {labels.undo ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={!canUndo}
                    aria-label={labels.undo}
                    onClick={undo}
                  >
                    <Undo2 className="mr-1 h-4 w-4" />
                    {labels.undo}
                  </Button>
                ) : null}
                {previewToolbarTrailing?.(toolbarContext)}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => requestDiscard("reset")}
                >
                  {labels.chooseAnother}
                </Button>
              </div>
            </div>

            {draftRows.length === 0 ? (
              <EmptyState
                variant="dashed"
                size="md"
                showIcon={false}
                title={labels.emptyPreview}
              />
            ) : (
              <div className="rounded-xl border border-surface-200">
                <DataTable<TRow, TSort>
                  labels={tableLabels}
                  closeLabel={t("close")}
                  loadingLabel={t("loading")}
                  rows={draftRows}
                  columns={columns}
                  getRowKey={(row) => row.clientKey}
                  defaultSortColumn={defaultSortColumn}
                  preferencesKey={preferencesKey}
                  paginate={false}
                  emptyMessage={labels.emptyPreview}
                  rowClassName={rowClassName}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {hasDiscardConfirmCopy ? (
        <ConfirmModal
          isOpen={discardConfirmOpen}
          onClose={() => {
            pendingDiscardActionRef.current = null;
            setDiscardConfirmOpen(false);
          }}
          onConfirm={confirmDiscard}
          title={labels.discardConfirmTitle!}
          message={labels.discardConfirmMessage!}
          confirmText={labels.discardConfirmAction!}
          cancelText={labels.discardConfirmCancel!}
          closeLabel={t("close")}
          isDestructive
        />
      ) : null}
    </>
  );
}
